import { useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DateInput } from "@/components/DateInput";
import { useAsyncData } from "@/lib/useAsyncData";
import Pagination from "@/components/Pagination";
import { ReportError, ReportLoading } from "@/components/leasing/ReportState";
import { fmtNumber, fmtDate } from "@/lib/format";
import { Download, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ReportL3({ financer }) {
  const [dateIn, setDateIn] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { loading, error, data, reload } = useAsyncData(async () => {
    const params = { financer, search, page, page_size: pageSize };
    if (dateIn) params.date_in = dateIn;
    const res = await api.get(`/leasing/reports/l3`, { params });
    return res.data;
  }, [financer, dateIn, search, page]);

  const doExport = async () => {
    try {
      await downloadFile(`/leasing/reports/l3/export?financer=${financer}`, `L3_${financer}.xlsx`);
    } catch (e) {
      toast.error("Export eșuat");
    }
  };

  const cols = [
    ["contract", "Contract", "l"],
    ["capital", "Valoare capital", "n"], ["dobanda", "Valoare dobândă", "n"],
    ["start_date", "Data start", "d"], ["end_date", "Data final", "d"],
    ["rata_lunara", "Rată lunară", "n"],
    ["sold_rata", "Sold 31.12.2020 rată", "n"], ["sold_dobanda", "Sold 31.12.2020 dob.", "n"],
    ["achitat_rata", "Achitat rată", "n"], ["achitat_dobanda", "Achitat dobândă", "n"],
    ["numar_rate", "Nr rate", "i"],
  ];

  const rows = data?.rows || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Data în (referință)</Label>
          <DateInput value={dateIn} onChange={setDateIn} className="w-40" data-testid="l3-date-in" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Caută contract</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="l3-search-input" placeholder="Contract…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-8 w-48" />
          </div>
        </div>
        {loading && <span className="flex items-center gap-1 text-xs text-slate-400 pb-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> se calculează…</span>}
        <Button onClick={doExport} variant="outline" className="gap-2 ml-auto" data-testid="l3-export-btn">
          <Download className="h-4 w-4" /> Export Excel
        </Button>
      </div>

      {error ? (
        <ReportError onRetry={reload} />
      ) : loading && !data ? (
        <ReportLoading />
      ) : (
        <>
        <div className="border rounded-md overflow-auto max-h-[68vh]">
          <table className="w-full text-xs jh-table" data-testid="report-l3-table">
            <thead className="sticky top-0 bg-slate-100">
              <tr>
                {cols.map((c) => (
                  <th key={c[0]} className={`px-3 py-2 whitespace-nowrap ${c[2] === "l" ? "text-left" : c[2] === "i" ? "text-center" : "text-right"}`}>{c[1]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-amber-50/40">
                  {cols.map((c) => {
                    const v = r[c[0]];
                    if (c[2] === "l") return <td key={c[0]} className="px-3 py-1.5 font-medium">{v}</td>;
                    if (c[2] === "d") return <td key={c[0]} className="px-3 py-1.5 text-right tabular">{fmtDate(v)}</td>;
                    if (c[2] === "i") return <td key={c[0]} className="px-3 py-1.5 text-center tabular">{v}</td>;
                    return <td key={c[0]} className="px-3 py-1.5 text-right tabular">{fmtNumber(v)}</td>;
                  })}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-slate-400">Niciun contract.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={pageSize} total={data?.total || 0} onPage={setPage} label="contracte" testid="l3-pagination" />
        </>
      )}
    </div>
  );
}
